import { useState } from 'react';
import { Check } from 'lucide-react';
import { ALL_NAV_PAGES } from '@/shell/navDefs';
import { useBarKeys, setBarKeys } from '@/lib/useNavPrefs';

// Tabbed settings shell. Tab one is the bottom-bar Menu editor; more tabs
// (notifications, display, etc.) slot in beside it later.
const TABS = [{ key: 'menu', label: 'Menu' }] as const;
type TabKey = (typeof TABS)[number]['key'];

function MenuEditor() {
  const barKeys = useBarKeys();
  const tools = ALL_NAV_PAGES.filter((p) => p.key !== 'home');

  const toggle = (key: string) => {
    setBarKeys(barKeys.includes(key) ? barKeys.filter((k) => k !== key) : [...barKeys, key]);
  };

  return (
    <div>
      <p className="mb-3 text-sm text-candy-ink-muted dark:text-candy-dark-ink-muted">
        Choose which tools appear in the bottom bar. Home and More are always shown. On a phone the
        first four selected appear in the bar; on a tablet up to seven. The rest live under More.
      </p>
      <div className="overflow-hidden rounded-xl border border-candy-line dark:border-candy-dark-line">
        {tools.map((p, i) => {
          const on = barKeys.includes(p.key);
          const Icon = p.icon;
          return (
            <button
              key={p.key}
              type="button"
              onClick={() => toggle(p.key)}
              aria-pressed={on}
              className={`flex min-h-[52px] w-full items-center gap-3 px-4 py-3 text-left ${
                i > 0 ? 'border-t border-candy-line dark:border-candy-dark-line' : ''
              } ${
                on
                  ? 'bg-candy-cream dark:bg-candy-dark-surface-warm'
                  : 'bg-candy-white dark:bg-candy-dark-surface'
              }`}
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-candy-cream-warm text-candy-pink dark:bg-candy-dark-surface-warm dark:text-candy-pink-dark">
                <Icon size={18} aria-hidden />
              </span>
              <span className="flex-1 text-[15px] text-candy-ink dark:text-candy-dark-ink">
                {p.label}
              </span>
              <span
                className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border ${
                  on
                    ? 'border-candy-pink bg-candy-pink text-white dark:border-candy-pink-dark dark:bg-candy-pink-dark dark:text-candy-dark-bg'
                    : 'border-candy-line text-transparent dark:border-candy-dark-line'
                }`}
                aria-hidden
              >
                <Check size={14} />
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export function Settings() {
  const [tab, setTab] = useState<TabKey>('menu');

  return (
    <div className="mx-auto w-full max-w-lg pb-24">
      <div className="mb-5">
        <h1 className="text-2xl font-semibold text-candy-ink dark:text-candy-dark-ink">Settings</h1>
        <p className="text-sm text-candy-ink-muted dark:text-candy-dark-ink-muted">
          Personalize your app.
        </p>
      </div>

      <div
        role="tablist"
        aria-label="Settings sections"
        className="mb-4 flex gap-1 border-b border-candy-line dark:border-candy-dark-line"
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${
              tab === t.key
                ? 'border-candy-pink text-candy-ink dark:border-candy-pink-dark dark:text-candy-dark-ink'
                : 'border-transparent text-candy-ink-muted dark:text-candy-dark-ink-muted'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'menu' && <MenuEditor />}
    </div>
  );
}
